import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import {
  Award, Sparkles, Lock, CheckCircle2, History, Loader2, Plus, Minus,
  CalendarClock, AlertTriangle, ArrowRight, Hourglass, Wallet,
} from 'lucide-react';

const fmt = (n) => (n ?? 0).toLocaleString('en-IN');
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; } };
const fmtD = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso || '—'; } };

const TXN_BADGE = {
  earned:             { label: 'Earned',    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  redeemed:           { label: 'Redeemed',  cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  expired:            { label: 'Expired',   cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  manual_adjustment:  { label: 'Adjustment',cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  reversal:           { label: 'Reversal',  cls: 'bg-orange-100 text-orange-700 border-orange-200' },
};

export default function RewardsTab({ patientId, user, onBooked }) {
  const isAdmin = user?.role === 'Admin';
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [bookReward, setBookReward] = useState(null);
  const [showAdjust, setShowAdjust] = useState(false);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const [w, l] = await Promise.all([
        api.get(`/rewards/wallet/${patientId}`),
        isAdmin ? api.get(`/rewards/ledger/${patientId}?limit=100`) : Promise.resolve({ data: [] }),
      ]);
      setWallet(w.data);
      setLedger(l.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load rewards');
    } finally { setLoading(false); }
  }, [patientId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-500" data-testid="rewards-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading rewards…
      </div>
    );
  }
  if (!wallet) return null;

  const eligible = wallet.eligible_rewards || [];
  const locked = wallet.locked_rewards || [];

  return (
    <div className="space-y-6" data-testid="rewards-tab">
      {/* Header */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Eden Reward Points</h3>
              <p className="text-xs text-slate-500">Earn 4% on every payment · redeem for select treatments</p>
            </div>
          </div>
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => setShowAdjust(true)}
              data-testid="rewards-manual-adjust-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Adjust Points
            </Button>
          )}
        </div>

        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Available" value={wallet.available_points} color="text-emerald-700" bg="bg-white" testid="rewards-stat-available" big />
          <Stat label="Lifetime Earned" value={wallet.lifetime_points_earned} color="text-slate-900" bg="bg-white" testid="rewards-stat-lifetime" />
          <Stat label="Redeemed" value={wallet.redeemed_points} color="text-purple-700" bg="bg-white" testid="rewards-stat-redeemed" />
          <Stat label="Expired" value={wallet.expired_points} color="text-rose-700" bg="bg-white" testid="rewards-stat-expired" />
        </div>

        {(wallet.expiring_in_7_days + wallet.expiring_in_15_days + wallet.expiring_in_30_days) > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-2">
            <ExpiringPill n={wallet.expiring_in_7_days}  label="in 7 days" />
            <ExpiringPill n={wallet.expiring_in_15_days} label="in 15 days" />
            <ExpiringPill n={wallet.expiring_in_30_days} label="in 30 days" />
          </div>
        )}
      </div>

      {/* Eligible rewards */}
      <Section title="Eligible Rewards" icon={<Sparkles className="w-5 h-5 text-emerald-600" />} count={eligible.length}>
        {eligible.length === 0 ? (
          <Empty text="No rewards unlocked yet. Keep earning points!" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {eligible.map((r) => (
              <RewardCard key={r.id} reward={r} status="eligible"
                onBook={() => setBookReward(r)} />
            ))}
          </div>
        )}
      </Section>

      {/* Locked rewards */}
      <Section title="Locked Rewards" icon={<Lock className="w-5 h-5 text-slate-500" />} count={locked.length}>
        {locked.length === 0 ? (
          <Empty text="All available rewards are unlocked." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {locked.map((r) => <RewardCard key={r.id} reward={r} status="locked" />)}
          </div>
        )}
      </Section>

      {/* Ledger (Admin only) */}
      {isAdmin && (
      <Section title="Reward Ledger" icon={<History className="w-5 h-5 text-indigo-600" />} count={ledger.length}>
        {ledger.length === 0 ? (
          <Empty text="No activity yet." />
        ) : (
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">When</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-right px-4 py-2">Change</th>
                  <th className="text-right px-4 py-2">Balance</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ledger.map((row) => {
                  const meta = TXN_BADGE[row.transaction_type] || { label: row.transaction_type, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                  return (
                    <tr key={row.id} className="hover:bg-slate-50" data-testid={`rewards-ledger-row-${row.id}`}>
                      <td className="px-4 py-2 text-xs text-slate-500">{fmtDT(row.created_at)}</td>
                      <td className="px-4 py-2">
                        <Badge className={`${meta.cls} text-xs border`}>{meta.label}</Badge>
                      </td>
                      <td className={`px-4 py-2 text-right font-semibold ${row.points_change >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {row.points_change >= 0 ? '+' : ''}{fmt(row.points_change)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-slate-900">{fmt(row.balance_after_transaction)}</td>
                      <td className="px-4 py-2 text-xs text-slate-500 hidden md:table-cell">
                        {row.reward_name ? <span className="font-medium text-slate-700">{row.reward_name}</span> : null}
                        {row.reward_name && row.remarks ? ' · ' : null}
                        {row.remarks}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      )}

      {bookReward && (
        <BookRewardModal
          patientId={patientId}
          reward={bookReward}
          onClose={() => setBookReward(null)}
          onBooked={() => { setBookReward(null); load(); onBooked && onBooked(); }}
        />
      )}

      {showAdjust && (
        <ManualAdjustModal
          patientId={patientId}
          onClose={() => setShowAdjust(false)}
          onSaved={() => { setShowAdjust(false); load(); }}
        />
      )}
    </div>
  );
}

// =====================================================================
// Inner pieces
// =====================================================================
const Stat = ({ label, value, color, bg, testid, big }) => (
  <div className={`${bg} rounded-xl border border-slate-200 p-3`} data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`${big ? 'text-3xl' : 'text-xl'} font-bold ${color}`}>{fmt(value)}</div>
  </div>
);

const ExpiringPill = ({ n, label }) => (
  <div className={`flex items-center justify-between px-3 py-2 rounded-xl border text-xs ${n > 0 ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-slate-200 bg-white text-slate-400'}`}>
    <span className="flex items-center gap-1">
      <Hourglass className="w-3 h-3" />
      Expiring {label}
    </span>
    <span className="font-semibold">{fmt(n)} pts</span>
  </div>
);

const Section = ({ title, icon, count, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h4 className="text-base font-semibold text-slate-900">{title}</h4>
      {typeof count === 'number' && <Badge variant="outline" className="text-xs">{count}</Badge>}
    </div>
    {children}
  </div>
);

const Empty = ({ text }) => (
  <div className="text-center py-6 bg-white rounded-xl border-2 border-dashed border-slate-200 text-sm text-slate-500">
    {text}
  </div>
);

const RewardCard = ({ reward, status, onBook }) => {
  const eligible = status === 'eligible';
  return (
    <div
      className={`rounded-2xl border p-4 transition shadow-sm hover:shadow-md ${eligible ? 'border-emerald-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-90'}`}
      data-testid={`reward-card-${reward.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h5 className="font-semibold text-slate-900">{reward.reward_name}</h5>
            {eligible
              ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Eligible</Badge>
              : <Badge className="bg-slate-200 text-slate-600 border-slate-300 text-xs">Locked</Badge>}
          </div>
          {reward.reward_description && (
            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{reward.reward_description}</p>
          )}
          <div className="text-xs text-slate-500 mt-2">
            Includes: <span className="text-slate-700">{reward.linked_service_name}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Required</div>
          <div className="text-xl font-bold text-amber-700">{fmt(reward.required_points)}</div>
          <div className="text-[10px] text-slate-500">points</div>
        </div>
      </div>

      {eligible ? (
        <Button
          onClick={onBook}
          className="mt-3 w-full rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
          data-testid={`reward-book-btn-${reward.id}`}
        >
          <CheckCircle2 className="w-4 h-4 mr-2" /> Book Reward
        </Button>
      ) : (
        <div className="mt-3 text-xs flex items-center justify-center gap-1 rounded-xl bg-white border border-slate-200 py-2 text-slate-600">
          <Lock className="w-3 h-3" /> Need <b className="text-slate-900 mx-1">{fmt(reward.needs_more_points)}</b> more points
        </div>
      )}
    </div>
  );
};

// ---------------- Book reward modal ----------------
function BookRewardModal({ patientId, reward, onClose, onBooked }) {
  const isTimeBased = reward.linked_service_type === 'time_based';
  const today = new Date(); today.setDate(today.getDate() + 1);
  const defaultDate = today.toISOString().slice(0, 10);

  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState(isTimeBased ? '10:00' : '');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!date) { toast.error('Please pick a date'); return; }
    if (isTimeBased && !time) { toast.error('Please pick a time'); return; }
    setSaving(true);
    try {
      const payload = {
        patient_id: patientId,
        reward_id: reward.id,
        scheduled_date: date,
        scheduled_time: isTimeBased ? time : null,
        remarks,
      };
      const res = await api.post('/rewards/redeem', payload);
      toast.success(`Reward booked! Appointment ${res.data.appointment.ref_number}`);
      onBooked && onBooked();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not book reward');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="book-reward-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-600" />
            Book Reward
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="font-semibold text-slate-900">{reward.reward_name}</div>
            <div className="text-xs text-slate-600 mt-1">
              Linked service: <b>{reward.linked_service_name}</b>
            </div>
            <div className="flex items-center gap-3 mt-3 text-sm">
              <span className="text-slate-600">Points required:</span>
              <span className="font-bold text-amber-700">{fmt(reward.required_points)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Appointment date</Label>
              <Input type="date" value={date} min={new Date().toISOString().slice(0,10)}
                     onChange={(e) => setDate(e.target.value)}
                     className="rounded-xl" data-testid="book-reward-date" />
            </div>
            {isTimeBased && (
              <div>
                <Label className="text-xs text-slate-600">Time</Label>
                <Input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                       className="rounded-xl" data-testid="book-reward-time" />
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs text-slate-600">Remarks (optional)</Label>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)}
                      rows={2} className="rounded-xl"
                      placeholder="e.g. patient prefers afternoon slot" />
          </div>

          <div className="text-xs text-slate-500 flex items-center gap-1">
            <ArrowRight className="w-3 h-3" />
            Appointment will be auto-confirmed, marked paid (₹0) with method “Reward Points”.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button
            disabled={saving}
            onClick={handleSubmit}
            className="rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
            data-testid="book-reward-confirm-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Confirm Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Manual adjustment modal ----------------
function ManualAdjustModal({ patientId, onClose, onSaved }) {
  const [direction, setDirection] = useState('add');
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const n = parseInt(points, 10);
    if (!Number.isFinite(n) || n <= 0) { toast.error('Enter a positive number of points'); return; }
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    setSaving(true);
    try {
      const signed = direction === 'add' ? n : -n;
      await api.post(`/admin/rewards/${patientId}/adjust`, {
        points_change: signed, reason: reason.trim(), remarks,
      });
      toast.success('Wallet adjusted');
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Adjustment failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="manual-adjust-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Manual Adjustment
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            <Button
              variant={direction === 'add' ? 'default' : 'outline'}
              onClick={() => setDirection('add')}
              className={`rounded-xl flex-1 ${direction==='add' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              data-testid="adjust-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
            <Button
              variant={direction === 'deduct' ? 'default' : 'outline'}
              onClick={() => setDirection('deduct')}
              className={`rounded-xl flex-1 ${direction==='deduct' ? 'bg-rose-600 hover:bg-rose-700' : ''}`}
              data-testid="adjust-deduct-btn"
            >
              <Minus className="w-4 h-4 mr-1" /> Deduct
            </Button>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Points</Label>
            <Input type="number" min="1" value={points} onChange={(e) => setPoints(e.target.value)}
                   className="rounded-xl" data-testid="adjust-points-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Reason</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
                   className="rounded-xl" placeholder="e.g. Goodwill / Correction"
                   data-testid="adjust-reason-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Remarks (optional)</Label>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className="rounded-xl" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button disabled={saving} onClick={handleSave}
                  className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  data-testid="adjust-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Adjustment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
